import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { Menu as MenuIcon, AdminPanelSettings } from '@mui/icons-material';
import { useAdminMode } from '@/hooks/useManage';
import { isPathActive, primaryNavItems } from './navigation';
import { MenuDrawer } from './MenuDrawer';

/** Ряд иконок; под ним полоса home-indicator (на iPhone 34px). */
export const BOTTOM_NAV_HEIGHT = 48;
/** Вся высота нижнего блока. Нужен `viewport-fit=cover`, иначе iOS отдаёт safe area = 0. */
export const BOTTOM_NAV_OFFSET = `calc(${BOTTOM_NAV_HEIGHT}px + max(8px, env(safe-area-inset-bottom)))`;
/** Небольшой боковой зазор, чтобы крайние иконки не садились в скругление. */
const EDGE_INSET_PX = 8;

const actionSx = {
  flex: 1,
  minWidth: 0,
  maxWidth: 'none',
  padding: '6px 0 2px',
  color: 'text.secondary',
  bgcolor: 'transparent',
  WebkitTapHighlightColor: 'transparent',
  transition: 'color 160ms ease',
  '&:hover, &:active, &.Mui-focusVisible': {
    bgcolor: 'transparent',
  },
  '&.Mui-selected': {
    paddingTop: '6px',
    color: 'primary.main',
    bgcolor: 'transparent',
  },
  '& .MuiBottomNavigationAction-label': {
    fontSize: '0.7rem',
    '&.Mui-selected': {
      fontSize: '0.7rem',
    },
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
};

/** Нижняя панель для ширины < lg (1024px). Видимость по брейкпоинту задаёт AppLayout. */
export const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminMode, canManage, toggle } = useAdminMode();
  const [menuOpen, setMenuOpen] = useState(false);

  const selected = primaryNavItems.find((item) => isPathActive(item.path, location.pathname))?.path ?? '';

  return (
    <>
      <Paper
        component="nav"
        sx={{
          display: { xs: 'block', lg: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderRadius: 0,
          pb: 'max(8px, env(safe-area-inset-bottom))',
          pl: `max(${EDGE_INSET_PX}px, env(safe-area-inset-left))`,
          pr: `max(${EDGE_INSET_PX}px, env(safe-area-inset-right))`,
          borderTop: 1,
          borderColor: 'divider',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={selected}
          showLabels
          sx={{ bgcolor: 'transparent', height: BOTTOM_NAV_HEIGHT }}
        >
          {primaryNavItems.map(({ id, path, label, icon: Icon }) => (
            <BottomNavigationAction
              key={id}
              value={path}
              label={label}
              icon={<Icon />}
              disableRipple
              onClick={() => navigate(path)}
              sx={actionSx}
            />
          ))}
          {canManage && (
            <BottomNavigationAction
              value="__admin__"
              label="Админ"
              icon={<AdminPanelSettings />}
              disableRipple
              onClick={toggle}
              aria-pressed={adminMode}
              sx={{
                ...actionSx,
                color: adminMode ? 'manage.main' : 'text.secondary',
              }}
            />
          )}
          <BottomNavigationAction
            value="__menu__"
            label="Меню"
            icon={<MenuIcon />}
            disableRipple
            onClick={() => setMenuOpen(true)}
            sx={{ ...actionSx, color: menuOpen ? 'primary.main' : 'text.secondary' }}
          />
        </BottomNavigation>
      </Paper>
      <MenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};
